import {
  worksheetApi,
  type WorksheetAssignSchedule,
  type WorksheetAssignScheduleBody,
  type WorksheetAssignScheduleListParams,
  type WorksheetAssignScheduleTarget,
} from '@mathflat/domain/Worksheet'

import type { Student, Worksheet } from '@/dto'
import dateUtils from '@/utils/DateUtils'

/** 학습지 하나에 걸린 대기 예약 한 건. 학원당 학습지 하나에 대기 예약은 1건이라 모달은 이것 하나 또는 null만 다룬다 */
export type PendingSchedule = {
  batchId: string
  worksheetId: Worksheet['id']
  scheduledAt: Date
  studentIds: Student['id'][]
}

export const foldPendingTargets = (targets: WorksheetAssignScheduleTarget[]): PendingSchedule[] => {
  const byBatch = new Map<string, PendingSchedule>()
  for (const { batchId, worksheetId, studentId, scheduledAt } of targets) {
    const found = byBatch.get(batchId)
    if (found) {
      found.studentIds.push(studentId)
    } else {
      byBatch.set(batchId, {
        batchId,
        worksheetId,
        scheduledAt: new Date(scheduledAt),
        studentIds: [studentId],
      })
    }
  }
  // 응답 순서에 좌우되지 않도록 예약 시각 오름차순으로 고정한다
  return [...byBatch.values()].sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime())
}

class WorksheetAssignScheduleController {
  getPendingSchedule = async (worksheetId: Worksheet['id']): Promise<PendingSchedule | null> => {
    const schedules = await this.getPendingTargets([worksheetId], [])
    return schedules[0] ?? null
  }

  getPendingTargets = async (
    worksheetIds: Worksheet['id'][],
    studentIds: Student['id'][],
  ): Promise<PendingSchedule[]> =>
    foldPendingTargets(
      await worksheetApi.getPendingWorksheetAssignScheduleTargets({
        worksheetIdList: worksheetIds,
        studentIdList: studentIds,
      }),
    )

  getTargets = async (query: WorksheetAssignScheduleListParams) =>
    await worksheetApi.getWorksheetAssignSchedules(query)

  getCounts = async () => await worksheetApi.getWorksheetAssignScheduleCount()

  create = async (body: WorksheetAssignScheduleBody) =>
    await worksheetApi.createWorksheetAssignSchedule(body)

  reschedule = async (batchId: WorksheetAssignSchedule['batchId'], scheduledAt: Date) => {
    await worksheetApi.rescheduleWorksheetAssignSchedule(batchId, {
      scheduledAt: dateUtils.formatLocalISO(scheduledAt),
    })
  }

  cancel = async (batchId: WorksheetAssignSchedule['batchId']) => {
    await worksheetApi.cancelWorksheetAssignSchedule(batchId)
  }

  cancelStudents = async (
    batchId: WorksheetAssignSchedule['batchId'],
    studentIds: Student['id'][],
  ) => {
    await worksheetApi.cancelWorksheetAssignScheduleStudents(batchId, { studentIdList: studentIds })
  }

  addStudents = async (batchId: WorksheetAssignSchedule['batchId'], studentIds: Student['id'][]) =>
    await worksheetApi.addWorksheetAssignScheduleStudents(batchId, { studentIdList: studentIds })
}

export const worksheetAssignScheduleController = new WorksheetAssignScheduleController()
